import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, ReceiptText, Save, Undo2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { InvoiceBadge } from "@/components/Badges";
import { formatDate } from "@/lib/format";
import { cn } from "@/lib/utils";
import api from "@/lib/api";

/**
 * InvoicePanel – ordrenr + «Fakturert» på ein lagra rapport (rapportdetalj).
 * PATCH /reports/{id}. onChange(updatedReport) lets the list refresh without a reload.
 */
export const InvoicePanel = ({ report, onChange, testid = "invoice-panel" }) => {
  const [order, setOrder] = useState(report?.order_number || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => setOrder(report?.order_number || ""), [report?.id, report?.order_number]);

  const dirty = (order || "").trim() !== (report?.order_number || "");

  const patch = async (body, okMsg) => {
    setBusy(true);
    try {
      const r = await api.patch(`/reports/${report.id}`, body);
      onChange?.(r.data);
      if (okMsg) toast.success(okMsg);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikkje lagre.");
    } finally {
      setBusy(false);
    }
  };

  if (!report) return null;
  return (
    <div className={cn("rounded-xl border p-3", report.invoiced ? "border-[rgba(31,138,76,0.35)] bg-[rgba(31,138,76,0.05)]" : "bg-muted/30")} data-testid={testid}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"><ReceiptText className="h-3.5 w-3.5" /> Ordre og fakturering</div>
        <InvoiceBadge invoiced={report.invoiced} />
      </div>
      <div className="mt-2 grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end">
        <div className="space-y-1">
          <Label htmlFor={`${testid}-order`} className="text-xs font-semibold">Ordrenr <span className="font-normal text-muted-foreground">– Tripletex/faktura</span></Label>
          <div className="flex gap-2">
            <Input id={`${testid}-order`} value={order} onChange={(e) => setOrder(e.target.value)} placeholder="t.d. 10245" className="h-10" autoComplete="off" data-testid={`${testid}-order-input`} />
            {dirty && (
              <>
                <Button size="sm" className="h-10" disabled={busy} onClick={() => patch({ order_number: order.trim() }, "Ordrenr lagra.")} data-testid={`${testid}-order-save`}>
                  {busy ? <Loader2 className="animate-spin" /> : <Save />} Lagre
                </Button>
                <Button size="icon" variant="ghost" className="h-10 w-10" onClick={() => setOrder(report.order_number || "")} aria-label="Angre" data-testid={`${testid}-order-undo`}><Undo2 /></Button>
              </>
            )}
          </div>
        </div>
        <label className="flex h-10 cursor-pointer items-center gap-3 rounded-lg border bg-card px-3 text-sm">
          <Switch checked={!!report.invoiced} disabled={busy} onCheckedChange={(v) => patch({ invoiced: v }, v ? "Rapporten er merkt som fakturert." : "Faktura-merking fjerna.")} data-testid={`${testid}-invoiced-switch`} />
          <span className="font-semibold">Fakturert</span>
        </label>
      </div>
      {report.invoiced && report.invoiced_at && (
        <p className="mt-2 text-xs text-muted-foreground" data-testid={`${testid}-invoiced-meta`}>Merkt fakturert {formatDate(report.invoiced_at)}{report.invoiced_by ? ` av ${report.invoiced_by}` : ""}.</p>
      )}
    </div>
  );
};
